const transformOrganizationInfo = (prospect, isKycRemedionForCompanyInfoEnabled) => {
  const {
    industryMultiSelect,
    numberOfEmployees,
    organisationContactDetails,
    ...rest
  } = prospect.organizationInfo;
  const { industry, subCategory } =
    industryMultiSelect &&
    industryMultiSelect.reduce(
      (industryAndSubCategory, item) => {
        item.industry?.length && industryAndSubCategory.industry.push(item.industry[0]);
        item.subCategory?.length && industryAndSubCategory.subCategory.push(item.subCategory[0]);
        return industryAndSubCategory;
      },
      { industry: [], subCategory: [] }
    );
  return {
    ...rest,
    industry,
    subCategory,
    ...(isKycRemedionForCompanyInfoEnabled && { numberOfEmployees, organisationContactDetails })
  };
};

const transformApplicationInfo = prospect => {
  const { accountType, rakValuePackage, islamicBanking } = prospect.applicationInfo;
  return { accountType, rakValuePackage, islamicBanking };
};

const transformSignatoryInfo = (
  prospect,
  viewId,
  currentIndex,
  isMultiPartnerJourney,
  isEditable
) => {
  const signatory = prospect.signatoryInfo[currentIndex];
  const signatoryList = prospect.signatoryInfo.map(signatory => {
    const {
      isSignatoryEmailIdChanged,
      isSignatoryMobileNumberChanged,
      ...cleanedSignatory
    } = signatory;
    return cleanedSignatory;
  });

  switch (viewId) {
    case "/ConsentInfo":
      return [
        {
          consentInfo: signatory.consentInfo,
          signatoryId: signatory.signatoryId
        }
      ];
    case "/StakeholdersAdditionalInfo":
      return [
        {
          stakeholderAdditionalInfo: signatory.stakeholderAdditionalInfo,
          showSOF: signatory.showSOF,
          signatoryId: signatory.signatoryId,
          kycDetails: signatory.kycDetails
        }
      ];
    case "/AccountInfo":
      return [
        {
          debitCardInfo: signatory.debitCardInfo,
          signatoryId: signatory.signatoryId
        }
      ];
    case "/StakeholdersInfo": {
      if (signatory) {
        const {
          isEFRCheckLimitExceeded,
          signatoryId,
          eFRCompletedTimestamp,
          consentInfo
        } = signatory;
        if (isEFRCheckLimitExceeded) {
          return [
            {
              isEFRCheckLimitExceeded,
              signatoryId,
              ...(isMultiPartnerJourney && { isApplicationFilled: true })
            }
          ];
        } else if (eFRCompletedTimestamp || consentInfo) {
          return [
            {
              eFRCompletedTimestamp,
              consentInfo,
              signatoryId,
              ...(isMultiPartnerJourney && { isApplicationFilled: true })
            }
          ];
        }
      }
      break;
    }
    case "/StakeholdersInfoPreview": {
      const {
        editedFullName,
        firstName,
        middleName,
        lastName,
        debitCardInfo,
        mothersMaidenName,
        kycDetails,
        isEFRDataCorrect,
        countryofBirth,
        maritalStatus,
        editedEmailId,
        editedMobileNumber,
        contactDetails,
        signatoryId
      } = signatory;

      const baseData = {
        editedFullName,
        debitCardInfo,
        mothersMaidenName,
        kycDetails,
        isEFRDataCorrect,
        countryofBirth,
        maritalStatus,
        signatoryId,
        firstName,
        middleName,
        lastName,
        ...(isMultiPartnerJourney && { isApplicationFilled: true })
      };

      if (!isMultiPartnerJourney) {
        kycDetails["isSignatory"] = true;
        kycDetails["isShareholder"] = true;
        kycDetails["shareHoldingPercentage"] = "100.00";
      }

      const additionalData = isEditable
        ? {}
        : {
            editedEmailId,
            editedMobileNumber,
            ...(isMultiPartnerJourney && { contactDetails })
          };

      return [
        {
          ...baseData,
          ...additionalData
        }
      ];
    }
    case "/PartnerDetailsInfo":
    case "/PartnershipSummaryInfo":
      return signatoryList;
    default: {
      const {
        editedFullName,
        debitCardInfo,
        mothersMaidenName,
        kycDetails,
        isEFRDataCorrect,
        countryofBirth,
        editedEmailId,
        editedMobileNumber,
        contactDetails,
        signatoryId
      } = signatory;
      return [
        {
          editedFullName,
          debitCardInfo,
          mothersMaidenName,
          kycDetails,
          isEFRDataCorrect,
          countryofBirth,
          editedEmailId,
          editedMobileNumber,
          ...(isMultiPartnerJourney && { contactDetails, signatoryId })
        }
      ];
    }
  }
};

const transformApplicantInfo = (prospect, viewId) => {
  if (viewId === "/AccountInfo" || viewId === "/SubmitApplication") {
    const { roCode, allianceCode, sourcingId } = prospect.applicantInfo;
    return { roCode, allianceCode, sourcingId };
  }
  return prospect.applicantInfo;
};

const transformAccountInfo = (prospect, isIslamicBanking, isCrossSellFeatureEnabled) => {
  const { receiveInterest, paymentSolutionList, creditCardList, ...rest } = prospect.accountInfo;
  if (isIslamicBanking) {
    return {
      ...rest,
      receiveInterest: false,
      ...(isCrossSellFeatureEnabled && { paymentSolutionList, creditCardList })
    };
  }
  return {
    ...rest,
    receiveInterest,
    ...(isCrossSellFeatureEnabled && { paymentSolutionList, creditCardList })
  };
};

const transformCompanyAdditionalInfo = (
  prospect,
  isTaxInfoKycRemediationEnabled,
  isHawalaKycRemediationEnabled,
  isOtherBankingKycRemediationEnabled,
  isBranchesAndSubsidiariesEnabled,
  isAgent
) => {
  const {
    vatRegistrationNo,
    uaeTinNo,
    corporateTaxRegistrationDate,
    dealingInHawala,
    authorizedHawalaBussiness,
    hawalaLicenseExpiry,
    otherBankAccountInfo,
    subsidiariesDetails,
    ...rest
  } = prospect.companyAdditionalInfo;
  return {
    ...rest,
    ...(isTaxInfoKycRemediationEnabled && {
      vatRegistrationNo,
      uaeTinNo,
      corporateTaxRegistrationDate
    }),
    ...(isHawalaKycRemediationEnabled && {
      dealingInHawala,
      authorizedHawalaBussiness,
      hawalaLicenseExpiry
    }),
    ...(isOtherBankingKycRemediationEnabled && { otherBankAccountInfo }),
    ...(isBranchesAndSubsidiariesEnabled && isAgent && { subsidiariesDetails })
  };
};

const transformProspectData = ({
  prospect,
  key,
  viewId,
  currentIndex,
  isMultiPartnerJourney,
  isEditable,
  isKycRemedionForCompanyInfoEnabled,
  isIslamicBanking,
  isCrossSellFeatureEnabled,
  isTaxInfoKycRemediationEnabled,
  isHawalaKycRemediationEnabled,
  isOtherBankingKycRemediationEnabled,
  isBranchesAndSubsidiariesEnabled,
  isAgent
}) => {
  switch (key) {
    case "organizationInfo":
      return transformOrganizationInfo(prospect, isKycRemedionForCompanyInfoEnabled);
    case "applicationInfo":
      return transformApplicationInfo(prospect);
    case "signatoryInfo":
      return transformSignatoryInfo(
        prospect,
        viewId,
        currentIndex,
        isMultiPartnerJourney,
        isEditable
      );
    case "kycAnnexure":
      return isAgent ? prospect.kycAnnexure : undefined;
    case "applicantInfo":
      return transformApplicantInfo(prospect, viewId);
    case "accountInfo":
      return transformAccountInfo(prospect, isIslamicBanking, isCrossSellFeatureEnabled);
    case "companyAdditionalInfo":
      return transformCompanyAdditionalInfo(
        prospect,
        isTaxInfoKycRemediationEnabled,
        isHawalaKycRemediationEnabled,
        isOtherBankingKycRemediationEnabled,
        isBranchesAndSubsidiariesEnabled,
        isAgent
      );
    case "sid":
      if (viewId === "/PartnershipSummaryInfo" || viewId === "/PartnerDetailsInfo") {
        return prospect[key];
      }
      break;
    default:
      return prospect[key];
  }
};

export { transformProspectData };
